import { captureException } from "@sentry/react-native";
import graphql from "babel-plugin-relay/macro";
import { useCallback } from "react";
import { useMutation } from "react-relay";
import { PayloadError } from "relay-runtime";

import {
  cardSaveRejected,
  cardSaveUnconfirmed,
  knownCardSaveError,
} from "../cardEntryErrors";
import {
  CreateUserCardOnFileInput,
  useCreateUserCardOnFileMutation,
} from "./__generated__/useCreateUserCardOnFileMutation.graphql";

const getPayloadFailure = (error: PayloadError) => {
  const extensions = "extensions" in error ? error.extensions : undefined;
  const code =
    typeof extensions === "object" &&
    extensions !== null &&
    "code" in extensions &&
    typeof extensions.code === "string"
      ? extensions.code
      : undefined;
  return knownCardSaveError(code) ?? knownCardSaveError(error.message);
};

/** Saves a card; the entry flow owns error presentation and navigation. */
export const useCreateUserCardOnFile = () => {
  const [createSquareCard, isCreatingSquareCard] =
    useMutation<useCreateUserCardOnFileMutation>(
      useCreateUserCardOnFileMutationString
    );

  const handleCreateSquareCard = useCallback(
    (input: CreateUserCardOnFileInput): Promise<string> =>
      new Promise((resolve, reject) => {
        createSquareCard({
          variables: { input },
          onCompleted: (response, errors) => {
            const payload = response.responsePayload;
            if (errors?.length) {
              const failures = errors.map(getPayloadFailure);
              const confirmedRejection = failures.every(Boolean)
                ? failures[0]
                : undefined;
              if (!confirmedRejection) captureException(errors);
              reject(confirmedRejection ?? cardSaveUnconfirmed());
            } else if (payload?.success && payload.squareCard?.id) {
              resolve(payload.squareCard.id);
            } else if (payload && !payload.success) {
              reject(
                knownCardSaveError(payload.errors?.[0]?.code) ??
                  knownCardSaveError(payload.errors?.[0]?.message) ??
                  cardSaveRejected()
              );
            } else {
              reject(cardSaveUnconfirmed());
            }
          },
          onError: (error) => {
            const failure = knownCardSaveError(error.message);
            if (!failure) captureException(error);
            reject(failure ?? cardSaveUnconfirmed());
          },
        });
      }),
    [createSquareCard]
  );

  return [handleCreateSquareCard, isCreatingSquareCard] as const;
};

const useCreateUserCardOnFileMutationString = graphql`
  mutation useCreateUserCardOnFileMutation($input: CreateUserCardOnFileInput!) {
    responsePayload: createUserCardOnFile(input: $input) {
      errors {
        field
        code
        message
      }
      success
      squareCard {
        id
        cardBrand
        lastFour
        enabled
        default
        ...SquareCardFragment
        ...SelectedPaymentMethodFragment
        user {
          ... on MemberUser {
            squareCards(enabled: true) {
              edges {
                node {
                  id
                  ...SquareCardFragment
                }
              }
            }
          }
        }
      }
    }
  }
`;
